import React from 'react';
import { Badge } from './ui/badge';
import { VACATION_TYPES, REQUEST_STATUS } from '../mock';
import { Calendar, Clock, FileText, CheckCircle2, XCircle, Hourglass, Stethoscope, User } from 'lucide-react';

export const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const typeLabel = (t) => VACATION_TYPES.find(x => x.value === t)?.label || t;

const statusBadge = (status) => {
  const cfg = REQUEST_STATUS[status] || REQUEST_STATUS.pending;
  const colorCls = {
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    red: 'bg-red-50 text-red-800 border-red-200'
  }[cfg.color];
  const Icon = status === 'approved' ? CheckCircle2 : status === 'rejected' ? XCircle : Hourglass;
  return (
    <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full border ${colorCls}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </div>
  );
};

export const VacationCard = ({ item, showEmployee = false, actions = null }) => (
  <div className="p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition-colors">
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0">
          <Calendar className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-900">{typeLabel(item.type)}</span>
            <span className="text-xs text-slate-500">• {item.days} {item.days === 1 ? 'giorno' : 'giorni'}</span>
          </div>
          {showEmployee && (
            <div className="flex items-center gap-1.5 text-xs text-slate-600 mt-0.5">
              <User className="w-3 h-3" />
              <span className="font-medium">{item.employeeName}</span>
              <span className="text-slate-400">({item.employeeEmail})</span>
            </div>
          )}
          <div className="text-xs text-slate-600 mt-1">
            <span className="font-medium">{formatDate(item.startDate)}</span>
            <span className="mx-1.5 text-slate-400">→</span>
            <span className="font-medium">{formatDate(item.endDate)}</span>
          </div>
        </div>
      </div>
      {statusBadge(item.status)}
    </div>

    {item.notes && (
      <div className="text-sm text-slate-700 bg-slate-50 rounded-md p-2.5 mb-2">
        <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide mb-1">Note</div>
        {item.notes}
      </div>
    )}

    {item.attachments && item.attachments.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mb-2">
        {item.attachments.map((a, i) => (
          <div key={`${a.name}-${i}`} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-800 border border-blue-100 rounded px-2 py-0.5">
            <FileText className="w-3 h-3" />
            {a.name}
          </div>
        ))}
      </div>
    )}

    {item.reviewNotes && (
      <div className={`text-sm rounded-md p-2.5 mb-2 ${item.status === 'approved' ? 'bg-emerald-50 text-emerald-900' : 'bg-red-50 text-red-900'}`}>
        <div className="text-[10px] uppercase font-semibold tracking-wide mb-1 opacity-80">Risposta HR</div>
        {item.reviewNotes}
      </div>
    )}

    <div className="flex items-center justify-between pt-1">
      <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
        <Clock className="w-3 h-3" />
        Inviata il {formatDate(item.createdAt)}
      </div>
      {actions}
    </div>
  </div>
);

export const SicknessCard = ({ item, showEmployee = false }) => (
  <div className="p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition-colors">
    <div className="flex items-start gap-3 mb-3">
      <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
        <Stethoscope className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-slate-900">Certificato di malattia</span>
          <span className="text-xs font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">#{item.protocolNumber}</span>
        </div>
        {showEmployee && (
          <div className="flex items-center gap-1.5 text-xs text-slate-600 mt-0.5">
            <User className="w-3 h-3" />
            <span className="font-medium">{item.employeeName}</span>
            <span className="text-slate-400">({item.employeeEmail})</span>
          </div>
        )}
        <div className="text-xs text-slate-600 mt-1">
          <span className="font-medium">{formatDate(item.startDate)}</span>
          {item.endDate && (
            <>
              <span className="mx-1.5 text-slate-400">→</span>
              <span className="font-medium">{formatDate(item.endDate)}</span>
            </>
          )}
        </div>
      </div>
    </div>

    {(item.doctor || item.notes) && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
        {item.doctor && (
          <div className="text-xs bg-slate-50 rounded-md p-2">
            <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide mb-0.5">Medico</div>
            <div className="text-slate-800">{item.doctor}</div>
          </div>
        )}
        {item.notes && (
          <div className="text-xs bg-slate-50 rounded-md p-2">
            <div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wide mb-0.5">Note</div>
            <div className="text-slate-800">{item.notes}</div>
          </div>
        )}
      </div>
    )}

    {item.attachments && item.attachments.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mb-2">
        {item.attachments.map((a, i) => (
          <div key={`${a.name}-${i}`} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-800 border border-blue-100 rounded px-2 py-0.5">
            <FileText className="w-3 h-3" />
            {a.name}
          </div>
        ))}
      </div>
    )}

    <div className="flex items-center gap-1.5 text-[11px] text-slate-400 pt-1">
      <Clock className="w-3 h-3" />
      Registrato il {formatDate(item.createdAt)}
    </div>
  </div>
);
